import { Grid, Box } from "@mui/material";
import type { RaceQuery } from "../../../generated/graphql";
import styles from './RaceFooter.module.css'
import qld from '../../../assets/states/qld.png'
import nsw from '../../../assets/states/nsw.png'
import vic from '../../../assets/states/vic.png'
import bm from '../../../assets/states/bm.png'
import wa from '../../../assets/states/wa.svg'
import { useRace } from "../../../hooks/useRace";
import clsx from "clsx";
import { formatCurrency } from '../../../constants/constants';
import type { useMeetingReturnType } from '../../../hooks/useMeeting';
import useAppState from "../../../hooks/useAppState";

interface Props {
  race: null | Omit<NonNullable<RaceQuery['race']>, '__typename'>;
  meeting: useMeetingReturnType;
}

export default function RaceFooter({ race, meeting}: Props) {
  const prev = meeting && race && race.number > 1 ? meeting.races[race.number - 2] : null

  const prevRace = useRace(prev?.id).race;

  const scratchedRunners = race?.competitors.filter((a) => {
    return a.scratched
  })

  const { controllerState } = useAppState();

  function findPoolSize(raceNo: number, type: "WIN" | "PLACE") {
    if (race?.number !== raceNo) return `$0.00k`
    const state = race?.meeting.track.state as "NSW" | "QLD" | "VIC" || "NSW"
    const stateMapping = {
      "VIC": "V",
      "NSW": "N",
      "QLD": "Q"
    }
    const pool = race.priceSets.find((a) => a.type === type && a.source === stateMapping[state])?.poolSize || 0
//    return `$${(pool / 100).toFixed(2)}k`
    return `${formatCurrency.format(pool)}`;
  }

  function findMarketPercentage() {
    const scratchedRunners = race?.competitors.filter((a) => a.scratched).map((a) => {return a.tabNo})
    const filteredPrices = race?.priceSets.find((a) => a.type === "WIN")?.prices.filter(price => !scratchedRunners?.includes(price.tabNo))
    if (!filteredPrices) return 0
    let marketPercentage = 0
    filteredPrices.forEach(x => {
      if (x.price) {
        marketPercentage += 1 / x.price
      }
    })
    return (marketPercentage * 100).toFixed(1)
  }

  function findSP(position: number) {
    const resultsToUse = prevRace?.results.find((a) => a.source === "OP")
    const tabNo = resultsToUse?.positions.find((a) => a.position === position)?.tabNo
    const sp = resultsToUse?.dividends.find((a) => a.dividendNumbers[0] === tabNo && a.type === "STARTING_PRICE")?.dividend
    return `$${sp?.toFixed(1) ?? 0}`
  }

  if (!race) return null;

  return (
    <Box sx={{ flexGrow: 0 }}  className={styles.footerContainer} >
      <Grid container spacing={0} columns={24} style={{ 'backgroundColor': 'white' }} direction="row" >
        <Grid sx={{ p: 1 }} xs={12} className={styles.resultContainer}>
          {prevRace !== null ? (
            <>
              <div className={styles.resultTitle}>
                <div className={clsx(styles[`trackName-${controllerState}`])}>
                  {prevRace.meeting.track.name} R{prevRace.number}
                </div>
                <div className={styles.trackStatus}>
                  {prevRace.status}
                </div>
              </div>
              <div className={styles.resultPositions}>
                {
                  findSP(1) !== '$0' && (
                    <div className={styles.resultPrices} style={{'marginRight': '2rem'}}>
                      <div className={styles.finishingPosition1 }>1</div>
                      <div className={styles.positionPrice}>
                    <span>
                      {prevRace.results[0].positions.find((a) => a.position === 1)?.tabNo} {' '}
                    </span>
                        <span style={{'color': 'green'}}>
                      SP {findSP(1)}
                    </span>
                      </div>
                    </div>
                  )
                }
                {
                  findSP(2) !== '$0' && (
                    <div className={styles.resultPrices}>
                      <div className={styles.finishingPosition2}>2</div>
                      <div className={styles.positionPrice}>
                    <span>
                      {prevRace.results[0].positions.find((a) => a.position === 2)?.tabNo} {' '}
                    </span>
                        <span style={{'color': 'green'}}>
                      SP {findSP(2)}
                    </span>
                      </div>
                    </div>
                  )
                }
                {
                  findSP(3) !== '$0' && (
                    <div className={styles.resultPrices}>
                      <div className={styles.finishingPosition3}>3</div>
                      <div className={styles.positionPrice}>
                    <span>
                      {prevRace.results[0].positions.find((a) => a.position === 3)?.tabNo} {' '}
                    </span>
                        <span style={{'color': 'green'}}>
                      SP {findSP(3)}
                    </span>
                      </div>
                    </div>
                  )
                }
              </div>
            </>
          ) : race.results.length > 0 ? (
            <div>No Previous Race</div>
          ) : (
            <div>No Previous Race</div>
          )}
        </Grid>
        <Grid xs={4} style={{ 'padding': '10px' }}>
          {
            controllerState === 'NSW' ? <img src={nsw} alt={'racing logo'} className={styles.racingLogo}/> :
            controllerState === 'QLD' ? <img src={qld} alt={'racing logo'} className={styles.racingLogo}/> :
            controllerState === 'VIC' ? <img src={vic} alt={'racing logo'} className={styles.racingLogo}/> : 
            controllerState === 'WA' ? <img src={wa} alt={'racing logo'} className={styles.racingLogo}/> : <img src={bm} alt={'racing logo'} className={styles.racingLogo}/>
          }
        </Grid>
        <Grid xs={8} className={clsx(styles[`poolsContainer-${controllerState}`])}>
          <div className={styles.marketPools}>
            <div>Market {findMarketPercentage()}%</div>
            <div className={styles.pools}>
              <div className={styles.poolsText}>{findPoolSize(race.number, "WIN")}</div>
              <div className={styles.poolsText}>{findPoolSize(race.number, "PLACE")}</div>
            </div>
          </div>
          <div>
            Scr: {scratchedRunners?.map((a, index) => {
              if (scratchedRunners.length === index + 1) return `${a.tabNo}`
              else return `${a.tabNo}, `
            })}
          </div>
        </Grid>
      </Grid>
    </Box>
  )
}